import { useState, useEffect, useCallback, useRef } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { productsAPI, inventoryAPI } from '../lib/api';
import { formatCurrency, formatDate, inventoryMovementLabels, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Search, 
  Plus, 
  Package, 
  AlertTriangle,
  ArrowUpCircle,
  ArrowDownCircle,
  Edit,
  Trash2,
  Loader2,
  Filter,
  ScanBarcode
} from 'lucide-react';
import { toast } from 'sonner';

export default function InventarioPage() {
  const { selectedBusiness } = useAuth();
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [showInsumos, setShowInsumos] = useState(false);
  
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [searchingBarcode, setSearchingBarcode] = useState(false);
  const barcodeInputRef = useRef(null);
  
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    sku: '',
    barcode: '',
    price: 0,
    cost: 0,
    category: '',
    stock: 0,
    min_stock: 5,
    is_for_sale: true,
    is_insumo: false,
    unit: 'unidad',
    total_content: '',
    content_unit: ''
  });

  const [movementForm, setMovementForm] = useState({
    product_id: '',
    type: 'entry',
    quantity: 1,
    notes: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const [productsRes, movementsRes, alertsRes] = await Promise.all([
        productsAPI.getAll({ is_insumo: showInsumos || undefined, low_stock: showLowStock }),
        inventoryAPI.getMovements({ limit: 50 }),
        inventoryAPI.getAlerts()
      ]);
      
      setProducts(productsRes.data);
      setMovements(movementsRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      logger.error('Error loading inventory:', error);
      toast.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, showLowStock, showInsumos]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openNewProduct = () => {
    setSelectedProduct(null);
    setBarcodeSearch('');
    setProductForm({
      name: '',
      description: '',
      sku: '',
      barcode: '',
      price: 0,
      cost: 0,
      category: '',
      stock: 0,
      min_stock: 5,
      is_for_sale: true,
      is_insumo: false,
      unit: 'unidad',
      total_content: '',
      content_unit: ''
    });
    setProductDialogOpen(true);
  };

  const handleBarcodeSearch = async () => {
    if (!barcodeSearch.trim()) return;
    
    setSearchingBarcode(true);
    try {
      const response = await productsAPI.getByBarcode(barcodeSearch.trim());
      const product = response.data;
      
      if (product) {
        // Producto encontrado - prellenar el formulario
        setSelectedProduct(product);
        setProductForm({
          name: product.name,
          description: product.description || '',
          sku: product.sku || '',
          barcode: product.barcode || '',
          price: product.price,
          cost: product.cost,
          category: product.category || '',
          stock: product.stock,
          min_stock: product.min_stock,
          is_for_sale: product.is_for_sale,
          is_insumo: product.is_insumo,
          unit: product.unit,
          total_content: product.total_content || '',
          content_unit: product.content_unit || ''
        });
        toast.success(`Producto encontrado: ${product.name}`);
      }
    } catch (error) {
      // Producto no encontrado - solo asignar el código de barras al formulario
      setProductForm(prev => ({ ...prev, barcode: barcodeSearch.trim() }));
      toast.info('Código no registrado. Puedes crear un nuevo producto.');
    } finally {
      setSearchingBarcode(false);
      setBarcodeSearch('');
    }
  };

  const handleBarcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeSearch();
    }
  };

  const openEditProduct = (product) => {
    setSelectedProduct(product);
    setBarcodeSearch('');
    setProductForm({
      name: product.name,
      description: product.description || '',
      sku: product.sku || '',
      barcode: product.barcode || '',
      price: product.price,
      cost: product.cost,
      category: product.category || '',
      stock: product.stock,
      min_stock: product.min_stock,
      is_for_sale: product.is_for_sale,
      is_insumo: product.is_insumo,
      unit: product.unit,
      total_content: product.total_content || '',
      content_unit: product.content_unit || ''
    });
    setProductDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || productForm.price < 0) {
      toast.error('Nombre y precio son requeridos');
      return;
    }

    try {
      const dataToSend = {
        ...productForm,
        total_content: productForm.total_content ? parseFloat(productForm.total_content) : null,
        content_unit: productForm.content_unit || null
      };
      
      if (selectedProduct) {
        await productsAPI.update(selectedProduct.id, dataToSend);
        toast.success('Producto actualizado');
      } else {
        await productsAPI.create({
          ...dataToSend,
          business_id: selectedBusiness.id
        });
        toast.success('Producto creado');
      }
      setProductDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;
    
    try {
      await productsAPI.delete(product.id);
      toast.success('Producto eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openMovementDialog = (product = null, type = 'entry') => {
    setMovementForm({
      product_id: product?.id || '',
      type,
      quantity: 1,
      notes: ''
    });
    setMovementDialogOpen(true);
  };

  const handleSaveMovement = async () => {
    if (!movementForm.product_id || movementForm.quantity <= 0) {
      toast.error('Producto y cantidad son requeridos');
      return;
    }

    try {
      await inventoryAPI.createMovement({
        ...movementForm,
        business_id: selectedBusiness.id
      });
      toast.success('Movimiento registrado');
      setMovementDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventario-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Inventario
          </h1>
          <p className="text-slate-500 mt-1">
            {products.length} productos • {alerts.length} alertas de stock
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => openMovementDialog(null, 'entry')}
            className="border-slate-200"
          >
            <ArrowUpCircle className="w-4 h-4 mr-2 text-emerald-400" />
            Entrada
          </Button>
          <Button 
            onClick={openNewProduct}
            className="bg-primary text-white hover:bg-primary/90"
            data-testid="new-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo producto
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <div>
                <p className="font-medium text-amber-400">Stock bajo</p>
                <p className="text-sm text-slate-500">
                  {alerts.length} producto(s) necesitan reabastecimiento
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-auto border-amber-500/30 text-amber-400"
                onClick={() => setShowLowStock(!showLowStock)}
              >
                {showLowStock ? 'Ver todos' : 'Ver alertas'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="products">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="products" className="data-[state=active]:bg-white data-[state=active]:text-black">
            <Package className="w-4 h-4 mr-2" />
            Productos
          </TabsTrigger>
          <TabsTrigger value="movements" className="data-[state=active]:bg-white data-[state=active]:text-black">
            <ArrowUpCircle className="w-4 h-4 mr-2" />
            Movimientos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-4 space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar productos..."
                className="pl-10 bg-white border-slate-200"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer">
                <Checkbox 
                  checked={showInsumos} 
                  onCheckedChange={setShowInsumos}
                  className="border-slate-300"
                />
                Solo insumos
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer">
                <Checkbox 
                  checked={showLowStock} 
                  onCheckedChange={setShowLowStock}
                  className="border-slate-300"
                />
                Stock bajo
              </label>
            </div>
          </div>

          {/* Products Table */}
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <Package className="w-12 h-12 mb-3 opacity-50" />
                  <p>No hay productos</p>
                </div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Producto</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Categoría</TableHead>
                      <TableHead>Precio</TableHead>
                      <TableHead>Costo</TableHead>
                      <TableHead>Stock</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.map((product, index) => (
                      <TableRow 
                        key={product.id}
                        className={cn(
                          "animate-fade-in",
                          product.stock <= product.min_stock && "bg-amber-500/5"
                        )}
                        style={{ animationDelay: `${index * 0.03}s` }}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                              <Package className="w-5 h-5 text-slate-500" />
                            </div>
                            <div>
                              <p className="font-medium">{product.name}</p>
                              {product.description && (
                                <p className="text-xs text-slate-500 truncate max-w-[200px]">
                                  {product.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm text-slate-500">
                            {product.sku || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-slate-500 border-slate-200">
                            {product.category || 'Sin categoría'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{formatCurrency(product.price)}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-slate-500">{formatCurrency(product.cost)}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                "font-bold",
                                product.stock <= product.min_stock ? "text-amber-400" : "text-white"
                              )}>
                                {product.stock}
                              </span>
                              <span className="text-slate-500 text-xs">{product.unit}</span>
                              {product.stock <= product.min_stock && (
                                <AlertTriangle className="w-4 h-4 text-amber-400" />
                              )}
                            </div>
                            {product.total_content && (
                              <div className="text-xs text-slate-500 mt-1">
                                {product.current_content?.toFixed(1) || 0} / {product.total_content} {product.content_unit}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <div className="flex gap-1">
                              {product.is_for_sale && (
                                <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30">
                                  Venta
                                </Badge>
                              )}
                              {product.is_insumo && (
                                <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-500/30">
                                  Insumo
                                </Badge>
                              )}
                            </div>
                            {product.barcode && (
                              <span className="text-[10px] text-slate-500 font-mono">{product.barcode}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 hover:bg-slate-100"
                              onClick={() => openMovementDialog(product, 'entry')}
                              title="Entrada"
                            >
                              <ArrowUpCircle className="w-4 h-4 text-emerald-400" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 hover:bg-slate-100"
                              onClick={() => openEditProduct(product)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 hover:bg-slate-100 hover:text-red-400"
                              onClick={() => handleDeleteProduct(product)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movements" className="mt-4">
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Cantidad</TableHead>
                    <TableHead>Stock anterior</TableHead>
                    <TableHead>Stock nuevo</TableHead>
                    <TableHead>Notas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map((movement, index) => {
                    const product = products.find(p => p.id === movement.product_id);
                    const isEntry = movement.type === 'entry';
                    return (
                      <TableRow 
                        key={movement.id}
                        className="animate-fade-in"
                        style={{ animationDelay: `${index * 0.03}s` }}
                      >
                        <TableCell>
                          <span className="text-sm text-slate-500">
                            {formatDate(movement.created_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{product?.name || 'Producto eliminado'}</span>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={cn(
                              isEntry ? "text-emerald-400 border-emerald-500/30" : "text-red-400 border-red-500/30"
                            )}
                          >
                            {inventoryMovementLabels[movement.type]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className={cn(
                            "font-bold",
                            isEntry ? "text-emerald-400" : "text-red-400"
                          )}>
                            {isEntry ? '+' : ''}{movement.quantity}
                          </span>
                        </TableCell>
                        <TableCell>{movement.previous_stock}</TableCell>
                        <TableCell>{movement.new_stock}</TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">{movement.notes || '-'}</span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[85vh] overflow-hidden p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {selectedProduct ? 'Editar producto' : 'Nuevo producto'}
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto max-h-[calc(85vh-140px)] px-6">
            <div className="space-y-4 py-4">
              {/* Barcode Scanner Section */}
              {!selectedProduct && (
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-3">
                  <div className="flex items-center gap-2 text-slate-500">
                    <ScanBarcode className="w-4 h-4" />
                    <span className="text-sm font-medium">Escanear código de barras</span>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      ref={barcodeInputRef}
                      type="text"
                      value={barcodeSearch}
                      onChange={(e) => setBarcodeSearch(e.target.value)}
                      onKeyDown={handleBarcodeKeyDown}
                      className="flex-1 bg-white border-slate-300 font-mono"
                      placeholder="Escanear o escribir código..."
                      autoFocus
                    />
                    <Button 
                      variant="outline" 
                      onClick={handleBarcodeSearch}
                      disabled={searchingBarcode}
                      className="border-slate-300"
                    >
                      {searchingBarcode ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Buscar'
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Si el producto existe, se cargarán sus datos. Si no, podrás crear uno nuevo.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SKU</Label>
                  <Input
                    value={productForm.sku}
                    onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                    className="bg-slate-100 border-slate-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Código de barras</Label>
                  <Input
                    value={productForm.barcode}
                    onChange={(e) => setProductForm({ ...productForm, barcode: e.target.value })}
                    className="bg-slate-100 border-slate-200"
                    placeholder="Código de barras"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Categoría</Label>
                <Input
                  value={productForm.category}
                  onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                  list="categories"
                />
                <datalist id="categories">
                  {categories.map(cat => <option key={cat} value={cat} />)}
                </datalist>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Precio de venta *</Label>
                  <Input
                    type="number"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-100 border-slate-200"
                    min={0}
                    step={0.01}
                  />
              </div>
              <div className="space-y-2">
                <Label>Costo</Label>
                <Input
                  type="number"
                  value={productForm.cost}
                  onChange={(e) => setProductForm({ ...productForm, cost: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-100 border-slate-200"
                  min={0}
                  step={0.01}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{selectedProduct ? 'Stock actual' : 'Stock inicial'}</Label>
                <Input
                  type="number"
                  value={productForm.stock}
                  onChange={(e) => setProductForm({ ...productForm, stock: parseInt(e.target.value) || 0 })}
                  className="bg-slate-100 border-slate-200"
                  min={0}
                  data-testid="product-stock-input"
                />
                {selectedProduct && (
                  <p className="text-xs text-slate-500">
                    Cambiarlo aquí registra un ajuste manual en el historial de movimientos.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Stock mínimo</Label>
                <Input
                  type="number"
                  value={productForm.min_stock}
                  onChange={(e) => setProductForm({ ...productForm, min_stock: parseInt(e.target.value) || 0 })}
                  className="bg-slate-100 border-slate-200"
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label>Unidad</Label>
                <Select 
                  value={productForm.unit} 
                  onValueChange={(value) => setProductForm({ ...productForm, unit: value })}
                >
                  <SelectTrigger className="bg-slate-100 border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="unidad">Unidad</SelectItem>
                    <SelectItem value="ml">Mililitros</SelectItem>
                    <SelectItem value="gr">Gramos</SelectItem>
                    <SelectItem value="lt">Litros</SelectItem>
                    <SelectItem value="kg">Kilogramos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Content tracking for consumables (gramaje) */}
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 space-y-4">
              <div className="flex items-center gap-2 text-slate-500">
                <Package className="w-4 h-4" />
                <span className="text-sm font-medium">Control de contenido (para insumos por gramaje/ml)</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contenido total del producto</Label>
                  <Input
                    type="number"
                    value={productForm.total_content}
                    onChange={(e) => setProductForm({ ...productForm, total_content: e.target.value })}
                    className="bg-slate-100 border-slate-200"
                    placeholder="Ej: 500 (para 500ml)"
                    min={0}
                    step={0.1}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unidad de contenido</Label>
                  <Select 
                    value={productForm.content_unit || 'select'} 
                    onValueChange={(value) => setProductForm({ ...productForm, content_unit: value === 'select' ? '' : value })}
                  >
                    <SelectTrigger className="bg-slate-100 border-slate-200">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="select">Seleccionar</SelectItem>
                      <SelectItem value="ml">Mililitros (ml)</SelectItem>
                      <SelectItem value="gr">Gramos (gr)</SelectItem>
                      <SelectItem value="oz">Onzas (oz)</SelectItem>
                      <SelectItem value="lt">Litros (lt)</SelectItem>
                      <SelectItem value="kg">Kilogramos (kg)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Cuando se use en formulaciones, se descontará del contenido disponible. Al agotarse, se reducirá el stock.
              </p>
            </div>

            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox 
                  checked={productForm.is_for_sale} 
                  onCheckedChange={(checked) => setProductForm({ ...productForm, is_for_sale: checked })}
                  className="border-slate-300"
                />
                <span className="text-sm">Disponible para venta</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox 
                  checked={productForm.is_insumo} 
                  onCheckedChange={(checked) => setProductForm({ ...productForm, is_insumo: checked })}
                  className="border-slate-300"
                />
                <span className="text-sm">Es insumo</span>
              </label>
            </div>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={() => setProductDialogOpen(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleSaveProduct} className="bg-primary text-white hover:bg-primary/90">
              {selectedProduct ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={movementDialogOpen} onOpenChange={setMovementDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Registrar movimiento</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Producto *</Label>
              <Select 
                value={movementForm.product_id} 
                onValueChange={(value) => setMovementForm({ ...movementForm, product_id: value })}
              >
                <SelectTrigger className="bg-slate-100 border-slate-200">
                  <SelectValue placeholder="Seleccionar producto" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {products.map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} (Stock: {product.stock})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de movimiento</Label>
                <Select 
                  value={movementForm.type} 
                  onValueChange={(value) => setMovementForm({ ...movementForm, type: value })}
                >
                  <SelectTrigger className="bg-slate-100 border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="entry">Entrada</SelectItem>
                    <SelectItem value="service_use">Salida-insumo</SelectItem>
                    <SelectItem value="waste">Merma / Caducó</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cantidad *</Label>
                <Input
                  type="number"
                  value={movementForm.quantity}
                  onChange={(e) => setMovementForm({ ...movementForm, quantity: parseInt(e.target.value) || 0 })}
                  className="bg-slate-100 border-slate-200"
                  min={1}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Input
                value={movementForm.notes}
                onChange={(e) => setMovementForm({ ...movementForm, notes: e.target.value })}
                className="bg-slate-100 border-slate-200"
                placeholder="Razón del movimiento..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMovementDialogOpen(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleSaveMovement} className="bg-primary text-white hover:bg-primary/90">
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
